// src/components/Sections/Admin/Usuarios/FichaUsuarioDrawer.jsx
import React, { useState, useEffect } from 'react';
import { Drawer, Tabs, Table, Spin, Alert, Tooltip } from 'antd';
import { MedicineBoxOutlined, LoadingOutlined } from '@ant-design/icons';
import {
  FiX, FiGrid, FiAward, FiCreditCard, FiUsers, FiCalendar, FiMapPin, FiClock, FiFileText,
} from 'react-icons/fi';
import OrdenCompraSvc from '../../../../service/OrdenCompra';
import VentaPresencialSvc from '../../../../service/VentaPresencial';
import DetalleVentaDrawer from '../Ventas/DetalleVentaDrawer';
import { useNotify } from '../../../../providers/NotificationProvider';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';
import FichaUsuarioSvc from '../../../../service/FichaUsuario';
import InscripcionesPorPersona from './InscripcionesPorPersona';
import { Iniciales } from '../../../Ui/Admin/PiezasAdmin';
import { formatearNombre } from '../../../../utils/texto';

dayjs.extend(relativeTime);
dayjs.locale('es');

const formatCLP = (n) => (n != null ? `$${Number(n).toLocaleString('es-CL')}` : '–');

/* Chips de estado: mismo lenguaje que el resto del panel.
   azul = al día · amarillo = en curso/atención · gris = terminado · rojo = falló */
const TONOS = {
  azul: 'bg-blue-50 text-primary',
  amarillo: 'bg-secondary/40 text-[#5c4300]',
  gris: 'bg-gray-100 text-gray-500',
  rojo: 'bg-red-50 text-red-700',
};
const Chip = ({ tono = 'gris', children }) => (
  <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-bold whitespace-nowrap ${TONOS[tono]}`}>{children}</span>
);

const MATRICULA = {
  activa: ['azul', 'Activa'], renovada: ['azul', 'Renovada'],
  vencida: ['gris', 'Vencida'], cancelada: ['rojo', 'Cancelada'],
};
const ORDEN = {
  completada: ['azul', 'Completada'], creada: ['gris', 'Creada'],
  procesando_pago: ['amarillo', 'Procesando'], fallida: ['rojo', 'Fallida'], cancelada: ['gris', 'Cancelada'],
  pendiente: ['amarillo', 'Pendiente'], reservada: ['amarillo', 'Reservada'], anulada: ['gris', 'Anulada'], expirada: ['gris', 'Expirada'],
  sin_completar: ['gris', 'Pago sin completar'],
};
const chipDe = (mapa, v) => { const [t, l] = mapa[v] || ['gris', v || '—']; return <Chip tono={t}>{l}</Chip>; };

const Vacio = ({ titulo, texto }) => (
  <div className="py-10 text-center">
    <p className="font-bold text-gray-900 m-0">{titulo}</p>
    {texto && <p className="text-sm text-gray-500 m-0 mt-1">{texto}</p>}
  </div>
);

/* ── Pestañas ──────────────────────────────────────────────────────────── */

const TabMatriculas = ({ data }) => {
  if (!data.length) return <Vacio titulo="Sin matrículas" texto="Esta persona no tiene matrículas de recinto." />;
  return (
    <ul className="m-0 p-0 list-none space-y-2.5">
      {data.map(m => {
        const vence = m.fechaVencimiento ? dayjs(m.fechaVencimiento) : null;
        const dias = vence ? vence.diff(dayjs(), 'day') : null;
        return (
          <li key={m._id} className="flex items-start justify-between gap-3 p-4 rounded-2xl bg-white border border-gray-100">
            <div className="min-w-0">
              <p className="flex items-center gap-1.5 font-bold text-gray-900 m-0">
                <FiMapPin size={14} className="text-primary flex-shrink-0" /> {formatearNombre(m.recinto?.nombre) || '—'}
              </p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs text-gray-600">
                <span className="capitalize">{m.tipoVigencia || '—'}</span>
                {m.fechaInicio && <span className="tabular-nums">Desde {dayjs(m.fechaInicio).format('DD/MM/YYYY')}</span>}
                <span className={`tabular-nums ${dias !== null && dias < 0 ? 'text-red-600' : dias !== null && dias <= 30 ? 'text-[#92400e] font-bold' : ''}`}>
                  {vence ? `Vence ${vence.format('DD/MM/YYYY')}${dias !== null && dias >= 0 && dias <= 30 ? ` (en ${dias} días)` : ''}` : 'Sin vencimiento'}
                </span>
                <span className="font-semibold text-gray-900">{formatCLP(m.valorPagado)}</span>
              </div>
            </div>
            {chipDe(MATRICULA, m.estado)}
          </li>
        );
      })}
    </ul>
  );
};

// Las boletas no vienen en la ficha: están en el detalle de cada venta (el mismo que
// usa Reportería). Online y presencial las guardan en lugares distintos.
const documentosDe = (detalle, esOnline) => (esOnline
  ? (detalle?.documentosBoleta || detalle?.pago?.facturacion?.documentosGenerados || [])
  : (detalle?.facturacion?.documentosGenerados || []));
// La pública primero: `urlPdf` es el enlace privado de Bsale y no abre sin sesión.
const urlDe = (doc) => doc?.urlPublica || doc?.urlPdf;

// Un pago que lleva horas en "procesando" no se está procesando: el usuario salió
// de Webpay sin terminar. Mostrarlo como "Procesando" hace creer que sigue en curso.
const HORAS_ABANDONO = 2;
const estadoVisible = (estado, fecha) => (
  ['procesando_pago', 'creada'].includes(estado) && fecha && dayjs().diff(dayjs(fecha), 'hour') >= HORAS_ABANDONO
    ? 'sin_completar'
    : estado
);

const TabOrdenes = ({ ordenes, ventasPresenciales }) => {
  const notify = useNotify();
  const [ventaDetalle, setVentaDetalle] = useState(null);
  const [buscandoBoleta, setBuscandoBoleta] = useState(null);

  const data = [
    ...ordenes.map(o => ({
      _id: o._id, numero: o.numeroOrden, tipo: 'Online', tipoVenta: 'online', estado: o.estado,
      monto: o.resumen?.total || 0, fecha: o.fechaCompletada || o.createdAt,
    })),
    ...ventasPresenciales.map(v => ({
      _id: v._id, numero: v.codigoVenta, tipo: 'Presencial', tipoVenta: 'presencial',
      // Presencial usa 'pagada' donde online usa 'completada'.
      estado: ['completada', 'pagada'].includes(v.estado) ? 'completada' : v.estado,
      monto: v.montoTotal || 0, fecha: v.fechaPago || v.createdAt,
    })),
  ].sort((a, b) => new Date(b.fecha) - new Date(a.fecha));

  const abrirDetalle = (r) => setVentaDetalle({ id: r._id, tipoVenta: r.tipoVenta });

  // Una boleta → se abre directo. Varias (una por taller) → el detalle, que las lista.
  const verBoleta = async (r) => {
    setBuscandoBoleta(r._id);
    try {
      const esOnline = r.tipoVenta === 'online';
      const detalle = esOnline
        ? await OrdenCompraSvc.GetDetalle(r._id).then(x => x?.data ?? x)
        : await VentaPresencialSvc.ObtenerVenta(r._id).then(x => x?.data?.data ?? x?.data ?? x);
      const conUrl = documentosDe(detalle, esOnline).filter(urlDe);
      if (conUrl.length === 1) window.open(urlDe(conUrl[0]), '_blank', 'noopener');
      else if (conUrl.length > 1) abrirDetalle(r);
      else notify.warning('Esta compra aún no tiene boleta emitida');
    } catch {
      notify.error('No se pudo obtener la boleta');
    } finally {
      setBuscandoBoleta(null);
    }
  };

  if (!data.length) return <Vacio titulo="Sin transacciones" texto="Esta persona no tiene compras online ni presenciales." />;

  const columns = [
    {
      title: 'Fecha', dataIndex: 'fecha', width: 150,
      render: (v) => v ? (
        <span className="text-sm text-gray-900 tabular-nums">{dayjs(v).format('DD/MM/YYYY')}
          <span className="block text-xs text-gray-500">{dayjs(v).format('HH:mm')}</span></span>
      ) : '–',
    },
    {
      title: 'Orden', dataIndex: 'numero',
      render: (v, r) => (
        <div>
          <span className="font-mono text-xs text-gray-700">{v}</span>
          <span className="block text-xs text-gray-500">{r.tipo}</span>
        </div>
      ),
    },
    {
      title: 'Estado', dataIndex: 'estado', width: 160,
      render: (v, r) => {
        const visible = estadoVisible(v, r.fecha);
        return visible === 'sin_completar' ? (
          <Tooltip title={`El pago se inició el ${dayjs(r.fecha).format('DD/MM/YYYY HH:mm')} y nunca se confirmó (quedó en «${v}»). Normalmente es un pago abandonado; si la persona reclama un cobro, revísalo en Operaciones.`}>
            <span className="cursor-help">{chipDe(ORDEN, visible)}</span>
          </Tooltip>
        ) : chipDe(ORDEN, visible);
      },
    },
    {
      title: 'Total', dataIndex: 'monto', width: 110, align: 'right',
      render: (v) => <span className="font-bold text-gray-900 tabular-nums">{formatCLP(v)}</span>,
    },
    {
      title: <span className="sr-only">Boleta</span>, key: 'boleta', width: 110, align: 'right',
      render: (_, r) => r.estado === 'completada' ? (
        <button type="button" disabled={buscandoBoleta === r._id}
          onClick={(e) => { e.stopPropagation(); verBoleta(r); }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 text-xs font-bold text-gray-700 hover:border-primary hover:!text-primary transition-colors disabled:opacity-60">
          {buscandoBoleta === r._id ? <LoadingOutlined /> : <FiFileText size={13} />} Boleta
        </button>
      ) : null,
    },
  ];

  return (
    <>
      <div className="admin-table-card">
        <Table dataSource={data} columns={columns} rowKey="_id" size="middle" scroll={{ x: 620 }}
          rowClassName={() => 'fila-clicable'}
          onRow={(r) => ({ onClick: () => abrirDetalle(r) })}
          pagination={data.length > 8 ? { pageSize: 8, size: 'small', style: { padding: '8px 12px' } } : false} />
      </div>
      <p className="text-xs text-gray-500 mt-2 mb-0">Haz clic en una fila para ver el detalle de la venta.</p>
      <DetalleVentaDrawer venta={ventaDetalle} onClose={() => setVentaDetalle(null)} />
    </>
  );
};

const TabFamiliares = ({ data }) => {
  if (!data.length) return <Vacio titulo="Sin familiares" texto="Esta cuenta no tiene cargas vinculadas." />;
  return (
    <ul className="m-0 p-0 list-none grid grid-cols-1 sm:grid-cols-2 gap-2.5">
      {data.map(f => {
        const edad = f.fechaNacimiento ? dayjs().diff(dayjs(f.fechaNacimiento), 'year') : null;
        const salud = (f.enfermedadesBases || '').trim();
        const declaraSalud = salud !== '' && salud.toLowerCase() !== 'ninguna';
        const categoria = edad === null ? null : edad < 18 ? 'Menor' : edad >= 60 ? 'Adulto mayor' : 'Adulto';
        return (
          <li key={f._id} className="p-4 rounded-2xl bg-white border border-gray-100">
            <div className="flex items-center gap-3">
              <Iniciales nombre={f.nombre} />
              <div className="min-w-0">
                <p className="font-bold text-gray-900 m-0 truncate">{formatearNombre(f.nombre)}</p>
                <p className="text-xs text-gray-500 m-0 font-mono">{f.rut}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {categoria && <Chip tono="amarillo">{categoria}{edad !== null ? ` · ${edad} años` : ''}</Chip>}
              {f.genero && <Chip><span className="capitalize">{f.genero}</span></Chip>}
            </div>
            {declaraSalud && (
              <div className="flex items-start gap-2 mt-3 p-2.5 rounded-xl bg-[#fff7ed] border border-[#fed7aa] text-xs text-[#9a3412]">
                <MedicineBoxOutlined className="mt-0.5 flex-shrink-0" /> <span>{salud}</span>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
};

/* ── Componente principal ─────────────────────────────────────────────── */

const Etiqueta = ({ icon, texto, n }) => (
  <span className="inline-flex items-center gap-2">
    {icon} {texto}
    {n > 0 && <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-gray-100 text-gray-600 text-[11px] font-bold inline-flex items-center justify-center">{n}</span>}
  </span>
);

const FichaUsuarioDrawer = ({ usuario, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const open = !!usuario;
  const angosto = typeof window !== 'undefined' && window.innerWidth < 900;

  useEffect(() => {
    if (!usuario?.userId) return;
    setLoading(true);
    setError(null);
    setData(null);
    FichaUsuarioSvc.getFicha(usuario.userId)
      .then(setData)
      .catch(() => setError('No se pudo cargar la ficha del usuario.'))
      .finally(() => setLoading(false));
  }, [usuario?.userId]);

  const activo = usuario?.isActive === true || usuario?.isActive === 'true';
  const r = data?.resumen;

  const kpis = r ? [
    { label: 'Inscripciones vigentes', value: r.inscripcionesActivas, icon: <FiGrid size={17} /> },
    { label: 'Matrículas activas', value: r.matriculasActivas, icon: <FiAward size={17} /> },
    { label: 'Transacciones', value: r.totalOrdenes, icon: <FiCreditCard size={17} /> },
    { label: 'Familiares', value: r.totalFamiliares, icon: <FiUsers size={17} /> },
  ] : [];

  const tabs = data ? [
    {
      key: 'inscripciones',
      label: <Etiqueta icon={<FiGrid size={15} />} texto="Inscripciones" n={r.totalInscripciones} />,
      // Por RUT/usuarioId: encuentra las inscripciones donde es titular Y donde es inscrito.
      children: <InscripcionesPorPersona usuarioId={usuario?.userId} rut={usuario?.rut} />,
    },
    {
      key: 'matriculas',
      label: <Etiqueta icon={<FiAward size={15} />} texto="Matrículas" n={r.totalMatriculas} />,
      children: <TabMatriculas data={data.matriculas} />,
    },
    {
      key: 'ordenes',
      label: <Etiqueta icon={<FiCreditCard size={15} />} texto="Pagos" n={r.totalOrdenes} />,
      children: <TabOrdenes ordenes={data.ordenes} ventasPresenciales={data.ventasPresenciales} />,
    },
    {
      key: 'familiares',
      label: <Etiqueta icon={<FiUsers size={15} />} texto="Familiares" n={r.totalFamiliares} />,
      children: <TabFamiliares data={data.familiares} />,
    },
  ] : [];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={angosto ? '100%' : 880}
      destroyOnClose
      closable={false}
      title={null}
      styles={{ header: { display: 'none' }, body: { padding: 0, background: '#f4f6f9' } }}
    >
      {/* ── Cabecera ── */}
      <header className="relative bg-[#00266b] text-white px-6 pt-6 pb-5 overflow-hidden">
        <div aria-hidden className="absolute inset-0 opacity-[.08] pointer-events-none" style={{
          backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '22px 22px',
        }} />
        <button type="button" onClick={onClose} aria-label="Cerrar ficha"
          className="absolute top-4 right-4 w-9 h-9 inline-flex items-center justify-center rounded-full text-blue-100 hover:bg-white/10 hover:text-white transition-colors">
          <FiX size={18} />
        </button>
        <div className="relative flex items-center gap-4 pr-10">
          <Iniciales nombre={usuario?.name} className="!w-14 !h-14 !text-lg !bg-white !text-primary ring-4 ring-white/15" />
          <div className="min-w-0">
            <h2 className="text-xl sm:text-2xl font-extrabold text-white leading-tight m-0 truncate">
              {formatearNombre(usuario?.name) || 'Usuario'}
            </h2>
            <p className="text-sm text-blue-100 m-0 mt-0.5 truncate">
              {usuario?.email}{usuario?.rut && <span className="text-blue-200"> · {usuario.rut}</span>}
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${activo ? 'bg-white/15 text-white' : 'bg-white/10 text-blue-200'}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${activo ? 'bg-secondary' : 'bg-blue-300'}`} /> {activo ? 'Cuenta activa' : 'Deshabilitada'}
              </span>
              {usuario?.lastLogin && (
                <Tooltip title={dayjs(usuario.lastLogin).format('DD/MM/YYYY HH:mm')}>
                  <span className="inline-flex items-center gap-1 text-xs text-blue-100 cursor-help">
                    <FiClock size={12} /> Último acceso {dayjs(usuario.lastLogin).fromNow()}
                  </span>
                </Tooltip>
              )}
            </div>
          </div>
        </div>

        {r && (
          <div className="relative mt-5 flex items-end justify-between gap-4 pt-4 border-t border-white/10">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-widest text-secondary m-0">Gasto total</p>
              <p className="text-3xl font-extrabold m-0 tabular-nums">{formatCLP(r.gastoTotal)}</p>
            </div>
            {usuario?.createdAt && (
              <p className="flex items-center gap-1.5 text-xs text-blue-100 m-0">
                <FiCalendar size={12} /> Cliente desde {dayjs(usuario.createdAt).format('MMMM YYYY')}
              </p>
            )}
          </div>
        )}
      </header>

      <div className="p-5 sm:p-6">
        {loading && <div className="py-16 text-center"><Spin size="large" /></div>}
        {error && !loading && <Alert message={error} type="error" showIcon />}

        {data && !loading && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-5">
              {kpis.map(k => (
                <div key={k.label} className="flex items-center gap-3 p-3.5 rounded-2xl bg-white border border-gray-100">
                  <span className={`w-9 h-9 flex-shrink-0 rounded-xl flex items-center justify-center
                    ${k.value > 0 ? 'bg-blue-50 text-primary' : 'bg-gray-100 text-gray-400'}`}>{k.icon}</span>
                  <div className="min-w-0">
                    <p className="text-xl font-extrabold text-gray-900 leading-none m-0 tabular-nums">{k.value}</p>
                    <p className="text-[11px] font-semibold text-gray-500 m-0 mt-1 leading-tight">{k.label}</p>
                  </div>
                </div>
              ))}
            </div>

            <section className="rounded-3xl bg-white border border-gray-100 px-5 pb-5">
              <Tabs items={tabs} />
            </section>
          </>
        )}
      </div>
    </Drawer>
  );
};

export default FichaUsuarioDrawer;
